import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Get token from sessionStorage
const getToken = () => {
  const storedUser = sessionStorage.getItem("loggedUser");
  if (!storedUser) {
    throw new Error('No authentication token found');
  }
  
  const parsedUser = JSON.parse(storedUser);
  const token = parsedUser?.token || parsedUser?.data?.token || parsedUser?.user?.token;
  
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return token;
};

// Fetch vendor withdrawals
export const fetchVendorWithdrawals = createAsyncThunk(
  'vendorWithdrawals/fetchVendorWithdrawals',
  async (_, thunkAPI) => {
    try {
      const token = getToken();
      const response = await axios.get(`${BASE_URL}/dashboard/withdrawal_vendor`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

// Update vendor withdrawal status
export const updateVendorWithdrawalStatus = createAsyncThunk(
  'vendorWithdrawals/updateVendorWithdrawalStatus',
  async ({ id, status }, thunkAPI) => {
    try {
      const token = getToken();
      const response = await axios.post(`${BASE_URL}/dashboard/withdrawal_vendor/${id}`, { status }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

// Delete vendor withdrawal
export const deleteVendorWithdrawal = createAsyncThunk(
  'vendorWithdrawals/deleteVendorWithdrawal',
  async (id, thunkAPI) => {
    try {
      const token = getToken();
      const response = await axios.delete(`${BASE_URL}/dashboard/withdrawal_vendor/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  withdrawals: [],
  isLoading: false,
  error: null,
  success: null,
  updateLoading: false,
  deleteLoading: false
};

const vendorWithdrawalSlice = createSlice({
  name: 'vendorWithdrawals',
  initialState,
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vendor withdrawals
      .addCase(fetchVendorWithdrawals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVendorWithdrawals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.withdrawals = action.payload.data;
        state.error = null;
      })
      .addCase(fetchVendorWithdrawals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || action.payload || 'حدث خطأ في تحميل طلبات التسوية';
      })
      
      // Update vendor withdrawal status
      .addCase(updateVendorWithdrawalStatus.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateVendorWithdrawalStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.success = action.payload.message || 'تم تحديث حالة الطلب بنجاح';
        state.error = null;
        // Update the withdrawal in the list
        const index = state.withdrawals.findIndex(w => w.id === action.payload.data.id);
        if (index !== -1) {
          state.withdrawals[index] = action.payload.data;
        }
      })
      .addCase(updateVendorWithdrawalStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload?.message || action.payload || 'حدث خطأ في تحديث حالة الطلب';
        state.success = null;
      })
      
      // Delete vendor withdrawal
      .addCase(deleteVendorWithdrawal.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteVendorWithdrawal.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.success = action.payload.message || 'تم حذف الطلب بنجاح';
        state.error = null;
        // Remove the withdrawal from the list
        state.withdrawals = state.withdrawals.filter(w => w.id !== action.payload.data.id);
      })
      .addCase(deleteVendorWithdrawal.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload?.message || action.payload || 'حدث خطأ في حذف الطلب';
        state.success = null;
      });
  },
});

export const { clearState } = vendorWithdrawalSlice.actions;
export default vendorWithdrawalSlice.reducer;
